import {
	useConfirmBrandFontUploadMutation,
	useGenerateBrandFontUploadUrlMutation,
} from '@codezo/graphql';
import { Upload, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Field } from '../../components/ui/field';
import { FileInput } from '../../components/ui/file-input';
import { Input } from '../../components/ui/input';
import { useFilePrepare } from '../../hooks/useFilePrepare';
import { readFontMetadata } from '../../lib/font-metadata';
import { FONT_ACCEPT, FONT_LABEL } from '../../lib/file-types';

/**
 * Add a font file to a brand.
 *
 * ── Why this isn't the asset uploader ──
 *
 * A font does not live where the other brand assets live. `confirmBrandFileUpload`
 * writes to `brandFiles`; a font belongs in `fontFiles`, which is a different
 * column and the only one the typography section reads. Sending an .otf through
 * the generic uploader therefore SUCCEEDS and then shows the file under Assets,
 * never under Typography — which looks exactly like the upload silently failing.
 *
 * So fonts get their own pair of mutations, and their own modal, because they
 * also need something no other asset does: the family.
 *
 * ── Why the fields fill themselves ──
 *
 * A typeface is a group of files sharing a family name — "Canela" holding a
 * Regular, an Italic and a Bold — so the family has to be right, and nobody
 * should have to type it.
 *
 * The file already knows: every OpenType font carries its family, weight and
 * style in its `name` table, which is what the OS reads for a font menu. That
 * is parsed on selection and fills all three. The filename guess still runs
 * first, because it is instant and covers `.woff2`, which cannot be parsed in
 * the browser — so the boxes are never empty while the file is being read.
 *
 * Everything stays editable: a trial font naming itself "GT America Trial" is
 * exactly the case where a human overrules the metadata.
 */

/** `Canela-BoldItalic.otf` → family "Canela", weight "Bold", style "Italic". */
function guessFromFilename(name: string): { family: string; weight: string; style: string } {
	const base = name.replace(/\.[^.]+$/, '');
	const [rawFamily, ...rest] = base.split(/[-_]/);
	const descriptor = rest.join(' ');

	const weight =
		/thin|extralight|ultralight|light|regular|book|medium|semibold|demibold|bold|extrabold|black|heavy/i.exec(
			descriptor
		)?.[0] ?? '';
	const style = /italic|oblique/i.test(descriptor) ? 'Italic' : '';

	const titleCase = (value: string) =>
		value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '';

	return {
		/* Split a run-together family too — "CanelaText" reads as "Canela Text". */
		family: rawFamily.replace(/([a-z])([A-Z])/g, '$1 $2').trim(),
		weight: titleCase(weight),
		style: titleCase(style),
	};
}

export function UploadBrandFontModal({
	open,
	onOpenChange,
	brandId,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	brandId: string;
	onSuccess?: () => void;
}) {
	const [file, setFile] = useState<File | null>(null);
	const [family, setFamily] = useState('');
	const [weight, setWeight] = useState('');
	const [style, setStyle] = useState('');
	const [uploading, setUploading] = useState(false);
	const { prepare, converting } = useFilePrepare();

	const [generateUploadUrl] = useGenerateBrandFontUploadUrlMutation();
	const [confirmUpload] = useConfirmBrandFontUploadMutation();

	const reset = useCallback(() => {
		setFile(null);
		setFamily('');
		setWeight('');
		setStyle('');
	}, []);

	/* Seeded on open, not on mount: the dialog keeps its tree between openings,
	   so without this the previous font's details are still in the boxes. */
	useEffect(() => {
		if (open) reset();
	}, [open, reset]);

	const selectFile = useCallback(async (picked: File) => {
		setFile(picked);

		/* The guess lands first so the fields are populated immediately, then
		   the font's own metadata replaces it. Each setter only overwrites a
		   value that is still blank or still the guess, so anything typed in
		   between survives — the read is async and the user is not blocked. */
		const guess = guessFromFilename(picked.name);
		setFamily((prev) => prev || guess.family);
		setWeight((prev) => prev || guess.weight);
		setStyle((prev) => prev || guess.style);

		const meta = await readFontMetadata(picked);
		if (!meta) return;

		const adopt = (next: string, guessed: string) => (prev: string) =>
			!prev || prev === guessed ? next : prev;

		setFamily(adopt(meta.family, guess.family));
		setWeight(adopt(meta.weight, guess.weight));
		setStyle(adopt(meta.style, guess.style));
	}, []);

	const handleDrop = useCallback(
		async (e: React.DragEvent) => {
			e.preventDefault();
			const picked = e.dataTransfer.files[0];
			if (!picked) return;
			/* Validated, not just hinted at by `accept` — a dragged-in PNG is
			   rejected here rather than queued for a font slot. */
			const [valid] = await prepare([picked], FONT_ACCEPT);
			if (valid) selectFile(valid);
		},
		[prepare, selectFile]
	);

	const canSave = Boolean(file && family.trim()) && !uploading;

	const handleSubmit = async () => {
		if (!file || !family.trim() || uploading) return;
		setUploading(true);

		try {
			const { data } = await generateUploadUrl({
				variables: {
					input: {
						brandId,
						fileName: file.name,
						fileType: file.type || 'font/otf',
						fileSize: file.size,
						fontFamily: family.trim(),
						fontWeight: weight.trim() || undefined,
						fontStyle: style.trim() || undefined,
					},
				},
			});

			const url = data?.generateBrandFontUploadUrl;
			if (!url?.uploadUrl || !url.blobUrl || !url.fileId) {
				throw new Error(url?.message || 'Failed to generate upload URL');
			}

			const res = await fetch(url.uploadUrl, {
				method: 'PUT',
				headers: {
					'x-ms-blob-type': 'BlockBlob',
					'Content-Type': file.type || 'application/octet-stream',
				},
				body: file,
			});
			if (!res.ok) throw new Error('Failed to upload font');

			await confirmUpload({
				variables: {
					input: {
						brandId,
						fontId: url.fileId,
						fileName: file.name,
						fontFamily: family.trim(),
						fontWeight: weight.trim() || undefined,
						fontStyle: style.trim() || undefined,
						blobUrl: url.blobUrl,
					},
				},
			});

			onSuccess?.();
			onOpenChange(false);
		} catch (err) {
			/* Surfaced by the caller's toast — rethrowing here would take the
			   dialog down with an unhandled rejection and lose the message. */
			// eslint-disable-next-line no-console
			console.error(err);
		} finally {
			setUploading(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Add font</DialogTitle>
					<DialogDescription>
						Upload a font file. Files sharing a family become one typeface.
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-5">
							<FileInput
								onFiles={(files) => {
									const picked = files[0];
									if (picked) selectFile(picked);
								}}
								accept={FONT_ACCEPT}
							>
								{({ open: pick, converting: fileConverting }) => (
									<div
										onDragOver={(e) => e.preventDefault()}
										onDrop={handleDrop}
										onClick={pick}
										className="flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed border-line-1 bg-surface-fill/50 px-4 py-8 text-center transition-colors hover:border-line-2 hover:bg-surface-hover"
									>
										{file ? (
											<div className="flex items-center gap-2 text-sm text-text-1">
												<span className="truncate font-medium">
													{file.name}
												</span>
												<button
													type="button"
													onClick={(e) => {
														e.stopPropagation();
														setFile(null);
													}}
													aria-label="Remove file"
													className="text-text-4 hover:text-text-1"
												>
													<X size={12} />
												</button>
											</div>
										) : (
											<>
												<div className="flex h-10 w-10 items-center justify-center rounded-full border border-line-1 bg-surface text-text-3">
													<Upload size={18} />
												</div>
												<div>
													<div className="text-sm font-medium text-text-2">
														{converting || fileConverting
															? 'Preparing…'
															: 'Drop a font here or click to browse'}
													</div>
													<div className="mt-1 text-xs text-text-4">
														{FONT_LABEL}
													</div>
												</div>
											</>
										)}
									</div>
								)}
							</FileInput>

							<Field label="Font family" required>
								<Input
									value={family}
									onChange={(e) => setFamily(e.target.value)}
									placeholder="e.g. Canela"
								/>
							</Field>

							<div className="grid gap-4 sm:grid-cols-2">
								<Field label="Weight">
									<Input
										value={weight}
										onChange={(e) => setWeight(e.target.value)}
										placeholder="e.g. Regular"
									/>
								</Field>
								<Field label="Style">
									<Input
										value={style}
										onChange={(e) => setStyle(e.target.value)}
										placeholder="e.g. Italic"
									/>
								</Field>
							</div>
						</div>
					</DialogBody>

					<DialogFooter>
						<div />
						<div className="flex gap-2">
							<Button
								type="button"
								variant="outline"
								onClick={() => onOpenChange(false)}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={!canSave}>
								{uploading ? 'Uploading…' : 'Add font'}
							</Button>
						</div>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
